import styled from "styled-components";

export const Products = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

export const ProductCard = styled.article`
  --product-card-height: 150px;

  display: grid;
  grid-template-columns: 190px minmax(0, 1fr);
  overflow: hidden;
  min-width: 0;
  min-height: var(--product-card-height);
  border: 1px solid
    ${({ $promoted }) => ($promoted ? "var(--app-yellow)" : "var(--app-line)")};
  border-radius: 14px;
  background: ${({ $promoted }) =>
    $promoted
      ? "linear-gradient(105deg, rgba(227, 183, 63, 0.12), var(--app-plate) 52%)"
      : "var(--app-plate)"};
  box-shadow: ${({ $promoted }) =>
    $promoted ? "inset 4px 0 0 var(--app-yellow)" : "none"};

  @media (max-width: 576px) {
    --product-card-height: 146px;

    grid-template-columns: 112px minmax(0, 1fr);
  }
`;

export const PromotedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  margin-bottom: 8px;
  padding: 4px 8px;
  border-radius: 7px;
  color: #594300;
  background: rgba(227, 183, 63, 0.3);
  font-family: "DM Mono", monospace;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  line-height: 1;
  text-transform: uppercase;
`;

export const ProductItem = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 16px;
  border-bottom: 1px solid var(--app-line);
  padding: 16px 0;
  margin: 0;

  &:last-child {
    border-bottom: none;
  }
`;

export const ProductImage = styled.div`
  background-image: url(${(props) => props.url});
  background-size: cover;
  background-position: center;
  width: 190px;
  height: 100%;
  min-height: var(--product-card-height);

  @media (max-width: 576px) {
    width: 112px;
  }
`;

export const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px 18px;

  > strong {
    display: block;
    font-size: 1rem;
    font-weight: 600;
  }
  > p {
    margin: 6px 0 15px;
    color: var(--app-muted);
    font-size: 0.82rem;
    line-height: 1.45;
  }
  > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: auto;
  }

  @media (max-width: 576px) {
    padding: 13px;
  }

  &:only-child {
    grid-column: 1 / -1;
    box-sizing: border-box;
    min-height: var(--product-card-height);
  }
`;

export const ProductPrice = styled.span`
  font-family: "DM Mono", monospace;
  font-size: 0.78rem;
  font-weight: 500;
`;

export const ProductAction = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 13px;
  border: 1px solid var(--app-line);
  border-radius: 7px;
  background: rgba(213, 209, 197, 0.2);
  color: var(--app-muted);
  font-family: "DM Sans", Arial, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  transition: background 220ms ease, border-color 220ms ease, color 220ms ease;

  &:hover {
    border-color: var(--app-ink);
    background: var(--app-plate);
    color: var(--app-ink);
  }
  &:focus-visible {
    outline: 3px solid rgba(242, 100, 60, 0.4);
    outline-offset: 2px;
  }

  svg {
    width: 17px;
    height: 17px;
    fill: none;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 1.75;
  }
`;

export const ProductQuantityControls = styled.div`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  overflow: hidden;
  border: 1px solid var(--app-line);
  border-radius: 7px;
  background: var(--app-control);

  ${ProductAction} {
    width: 34px;
    min-height: 32px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    color: var(--app-muted);
    font-size: 1rem;
    font-weight: 500;

    &:hover {
      border: 0;
      background: rgba(23, 25, 20, 0.06);
      color: var(--app-ink);
    }

    &:focus-visible {
      outline-offset: -3px;
    }

    svg {
      width: 15px;
      height: 15px;
    }
  }

  > span {
    align-self: stretch;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 30px;
    border-right: 1px solid var(--app-line);
    border-left: 1px solid var(--app-line);
    text-align: center;
    font-family: "DM Mono", monospace;
    font-size: 0.78rem;
    font-weight: 600;
  }
`;

export const CartProductItem = styled.li`
  border-bottom: 1px solid var(--app-line);
  padding: 18px 0;

  .cart-item__name {
    margin-bottom: 12px;
    font-size: 0.92rem;
    font-weight: 500;
    line-height: 1.35;
  }

  .cart-item__row {
    display: flex;
    align-items: center;
  }

  .cart-item__row {
    justify-content: space-between;
    gap: 16px;
  }
  .cart-item__price {
    font-family: "DM Mono", monospace;
    font-size: 0.78rem;
  }
`;

export const ProductCategory = styled(ProductItem)`
  align-items: baseline;
  margin-top: 38px;
  margin-bottom: 8px;
  padding: 0;
  border-bottom: 0;
  font-family: "DM Sans", Arial, sans-serif;
  font-size: 1.02rem;
  font-weight: 500;
  letter-spacing: -0.01em;

  &:first-child {
    margin-top: 28px;
  }

  small {
    color: var(--app-muted);
    font-family: "DM Mono", monospace;
    font-size: 0.58rem;
    font-weight: 400;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }
`;

export const DatePickerHeader = styled.div`
  .date-picker-hint {
    color: #aaada4;
    font-size: 0.75rem;
    line-height: 1.4;
  }

  min-height: 66px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;

  > div {
    display: grid;
    gap: 4px;
    min-width: 0;
  }

  strong {
    overflow: hidden;
    font-family: "DM Sans", Arial, sans-serif;
    font-size: 1.05rem;
    font-weight: 500;
    letter-spacing: -0.015em;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  @media (max-width: 480px) {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
    padding: 4px 0 12px;

    &:not(:has(> button)) {
      min-height: 64px;
      justify-content: center;
      padding: 0;
    }

    > button {
      width: 100%;
    }
  }
`;

export const DatePickerScroller = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  padding: 18px 24px 20px;
  scrollbar-width: thin;
`;

export const Scrollable = styled.div`
  overflow-x: auto;
  white-space: nowrap;
  scrollbar-width: thin;
`;

export const Badge = styled.div`
  padding: 4px 8px;
  display: inline-block;
  border: 1px solid ${(props) => props.color};
  color: ${(props) => props.color};
  font-family: "DM Mono", monospace;
  font-size: 0.72rem;
  border-radius: 4px;
`;

export const Circle = styled.div`
  position: relative;
  border-radius: 50%;
  padding: 10px;
  color: var(--app-ink);
  font-weight: 500;
  width: ${({ size = 50 }) => size + "px"};
  height: ${({ size = 50 }) => size + "px"};
  background: var(--app-herb);
  border: 1px solid var(--app-ink);
`;

export const Checkmark = styled.div`
  position: absolute;
  top: 45%;
  left: 29%;
  transform: rotate(45deg) translate(-50%, -50%);
  height: 25px;
  width: 15px;
  border-bottom: 2px solid var(--app-ink);
  border-right: 2px solid var(--app-ink);
`;
